import { UploadedDataset } from './ai-agent.service';

/**
 * SessionStorage key carrying an OPC bin dataset from the bin-analysis page to
 * the agent page. SessionStorage (not a query param) because the payload is a
 * whole matrix, and it is consumed once on arrival.
 */
const HANDOFF_KEY = 'opc_bin_ai_handoff_v1';

/** A dataset plus the question to pre-fill, handed to the agent page. */
export type ChartAiHandoff = { dataset: UploadedDataset; prompt: string };

/**
 * Stash a handoff for the agent page to pick up on its next mount.
 * @param handoff - Dataset and pre-filled prompt.
 */
export function stashChartAiHandoff(handoff: ChartAiHandoff): void {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(handoff));
  } catch {
    // Quota or a privacy mode that blocks sessionStorage: the agent page still
    // opens, just without the dataset pre-loaded.
  }
}

/**
 * Read and clear the pending handoff. Clearing it means a later reload of the
 * agent page starts clean instead of silently re-attaching an old dataset.
 * @returns The handoff, or null when there is none.
 */
export function takeChartAiHandoff(): ChartAiHandoff | null {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY);
    const parsed = JSON.parse(raw) as ChartAiHandoff;
    if (!parsed?.dataset?.headers || !Array.isArray(parsed.dataset.rows)) return null;
    return parsed;
  } catch {
    return null;
  }
}
